import { useMutation, useQueryClient } from '@tanstack/react-query';
import { maxUint256 } from 'viem';
import { useWalletClient } from 'wagmi';
import { ERC20_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

/**
 * Returns a mutation function to approve the token for OFT send on the source chain.
 *
 * For Ethereum USDT (isAdapter=true): USDT requires allowance to be 0 before
 * setting a new non-zero value. This hook automatically resets to 0 first if
 * there's an existing non-zero allowance.
 *
 * Approves maxUint256 to avoid requiring re-approval for future sends.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 */
export function useOftApprove(sourceChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({
    chainId: sourceChainId,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!sourceChainId) {
        throw new Error('sourceChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[sourceChainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${sourceChainId}`);
      }

      // Ethereum USDT requires approve(0) before approve(newAmount) if
      // current allowance is non-zero. Check and reset if needed.
      if (chainConfig.isAdapter) {
        const publicClient = getSourceChainPublicClient(sourceChainId);
        const currentAllowance = await publicClient.readContract({
          address: chainConfig.tokenAddress,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [walletClient.account.address, chainConfig.oftAddress],
        });

        if (currentAllowance > BigInt("0")) {
          console.log('[OftApprove] Resetting USDT allowance to 0 first...');
          const resetTxHash = await walletClient.writeContract({
            address: chainConfig.tokenAddress,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [chainConfig.oftAddress, BigInt("0")],
            chain: walletClient.chain,
            account: walletClient.account,
          });
          // Wait for the reset tx to be mined
          await publicClient.waitForTransactionReceipt({ hash: resetTxHash });
          console.log('[OftApprove] Allowance reset to 0');
        }
      }

      const txHash = await walletClient.writeContract({
        address: chainConfig.tokenAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [chainConfig.oftAddress, maxUint256],
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return txHash;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['oftAllowance', sourceChainId],
      });
    },
  });
}
